import React, { PropsWithChildren, useState, useCallback, useEffect } from 'react';
import {
  reduxForm, Field, WrappedFieldProps, InjectedFormProps, FormErrors,
} from 'redux-form';
import { useTranslation } from 'react-i18next';

import Api, { FailedRequest } from '@common/services/api';
import Tooltip from '@common/components/tooltip';
import { useAppSelector } from '@common/hooks';
import * as validation from '../../../../common/utils/validation';
import * as Alert from '@common/services/alert';

import ModalContent from '@common/components/modal/modal-content';
import {
  Row, Group, Label, TextInput, SelectInput,
} from '@common/components/form';

import type { Network, OrganisationFunction } from '../../../../common/types/objects';
import { ShortcutTypes } from '@modules/admin/definitions';
import { combineClassNames } from '@common/utils/combineClassNames';
import { ApiResponse } from '@common/services/api/types';
import Spinner from '@common/components/spinner';
import Icon from '@common/components/icon';

type OwnProps = {
  onClose: () => void;
  onUpdate?: (id: string, updatedShortcut: any) => void;
  onAdd?: (newShortcut: any) => void;
};

export type FormData = {
  id?: string;
  name: string;
  functions: OrganisationFunction[];
  networks: Network[];
  web_url: string;
  icon: {
    url: string;
    color: string;
  };
};

type Props = InjectedFormProps<FormData, OwnProps, boolean> & OwnProps;

const availableColors = [
  '', '#3C9EEF', '#33B29C', '#42B35D', '#F6AE35', '#F78C36', '#F76E63', '#F77995', '#F57ECB', '#9E74FA',
];
const availableImages = [
  'external', 'account', 'money', 'lock', 'learn', 'heart', 'date', 'settings',
  'favorite', 'assignment', 'files', 'download', 'clothes', 'email', 'privacy',
  'statistics', 'structure', 'travel', 'organisation', 'cake',
];

const getUrl = (image: string, preview = false) => (
  `https://assets.oneteam.io/globals/attachments/default/shortcuts/${image}${preview ? '-preview' : ''}.png`);

const ImageSelectorInput = ({ input: { value, onChange } }: WrappedFieldProps) => (
  <div className="ShortcutForm__ImageSelector">
    {availableImages.map((image) => (
      <div
        key={image}
        onClick={() => onChange(image)}
        className={`ShortcutForm__ImageSelector__Item${image === value ? ' ShortcutForm__ImageSelector__Item--active' : ''}`}
      >
        <img src={getUrl(image, true)} alt="Icon" />
      </div>
    ))}
  </div>
);

const ColorInput = ({ input: { value, onChange } }: WrappedFieldProps) => (
  <div className="ShortcutForm__ColorSelector">
    {availableColors.map((color) => (
      <div
        key={color}
        className={combineClassNames('ShortcutForm__ColorSelector__Item', {
          'ShortcutForm__ColorSelector__Item--active': color === value,
        })}
        style={{ backgroundColor: color }}
        onClick={() => onChange(color)}
      />
    ))}
  </div>
);

const IconPreview = ({ input: { value: { url, color } } }: WrappedFieldProps) => (
  <div className="Shortcut__Icon" style={{ backgroundColor: color }}>
    <img src={getUrl(url)} alt="Icon" />
  </div>
);

const ShortcutEditor = ({
  valid,
  handleSubmit,
  onClose,
  onUpdate,
  onAdd,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const orgId = useAppSelector((state) => state.organisation.selected.id);

  const [networks, setNetworks] = useState<ApiResponse<Network[]> | null>(null);
  const [functions, setFunctions] = useState<ApiResponse<OrganisationFunction[]> | null>(null);
  useEffect(() => {
    Promise.all([
      Api.get<ApiResponse<Network[]>>(`/v3/organisations/${orgId}/networks`),
      Api.get<ApiResponse<OrganisationFunction[]>>(`/v3/organisations/${orgId}/functions`)
    ]).then(([networksResponse, functionsResponse]) => {
      setNetworks(networksResponse);
      setFunctions(functionsResponse);
    });
  }, [setNetworks, setFunctions, orgId]);

  const [submitting, setSubmitting] = useState(false);

  const onConfirm = useCallback(async (values: FormData) => {
    setSubmitting(true);
    const payload = {
      name: values.name,
      web_url: values.web_url,
      icon: values.icon.url,
      color: values.icon.color || null,
      network_ids: values.networks?.map((n) => n.id) || [],
      function_ids: values.functions.map((f) => f.id) || []
    };

    try {
      if (values.id) {
        const { data } = await Api.put(`/v4/organisations/${orgId}/shortcuts/${values.id}`, payload);
        Alert.success(t('organisation:forms_shortcut_edit_success'));
        if (onUpdate) onUpdate(values.id, data);
      } else {
        const { data } = await Api.post(`/v4/organisations/${orgId}/shortcuts`, {
          ...payload,
          type: ShortcutTypes.EXTERNAL_LINK,
        });
        if (onAdd) onAdd(data);
        Alert.success(t('organisation:forms_shortcut_create_success', { name: payload.name }));
      }
      onClose();
    } catch (e) {
      Alert.forStatus((e as FailedRequest).status_code, {
        warning: values.id
          ? t('organisation:forms_shortcut_edit_warning')
          : t('organisation:forms_shortcut_create_warning'),
        error: values.id
          ? t('organisation:forms_shortcut_edit_error')
          : t('organisation:forms_shortcut_create_error'),
      });
      setSubmitting(false);
      throw e;
    }
  }, [setSubmitting, t, onClose, onAdd, onUpdate, orgId]);

  const loading = !(networks && functions);
  return (
    <ModalContent
      title={t('organisation:forms_shortcut_title')}
      onHide={onClose}
      onConfirm={handleSubmit(onConfirm)}
      confirmButtonDisabled={!valid}
      confirmButtonLoading={submitting}
    >
      {
        loading ?
          (
            <Spinner centered size="large" />
          ) :
          (
            <>
              <Label text={t('organisation:forms_shortcut_information')} />
              <Row>
                <Field
                  name="icon"
                  component={IconPreview}
                />
                <Group>
                  <Row>
                    <Group>
                      <TextInput
                        name="name"
                        placeholder={t('organisation:forms_shortcut_name_placeholder')}
                      />
                      <div style={{ height: 8 }} />
                      <TextInput
                        name="web_url"
                        placeholder={t('organisation:forms_shortcut_web_url_placeholder')}
                      />
                    </Group>
                  </Row>
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label text={t('organisation:forms_shortcut_select_color')} />
                  <Field
                    name="icon.color"
                    component={ColorInput}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label text={t('organisation:forms_shortcut_select_image')} />
                  <Field
                    name="icon.url"
                    component={ImageSelectorInput}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label text={t('organisation:forms_shortcut_visibility_networks')} />
                  <Tooltip
                    placement="right"
                    title={t('organisation:shortcuts_modal_networks_tooltip')}
                  >
                    <Icon type="info" size="xsmall" className="Icon--titleIcon" />
                  </Tooltip>
                  <SelectInput
                    name="networks"
                    options={networks.data}
                    labelKey="name"
                    valueKey="id"
                    placeholder={t('organisation:forms_shortcut_visibility_networks_placeholder')}
                    multi
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label text={t('organisation:forms_shortcut_visibility_functions')} />
                  <Tooltip
                    placement="right"
                    title={t('organisation:shortcuts_modal_functions_tooltip')}
                  >
                    <Icon type="info" size="xsmall" className="Icon--titleIcon" />
                  </Tooltip>
                  <SelectInput
                    name="functions"
                    options={functions.data}
                    labelKey="name"
                    valueKey="id"
                    placeholder={t('organisation:forms_shortcut_visibility_functions_placeholder')}
                    multi
                  />
                </Group>
              </Row>
            </>
          )
      }
    </ModalContent>
  );
};

export default reduxForm<FormData, OwnProps, boolean>({
  form: 'add-shortcut',
  initialValues: {
    name: '',
    networks: [],
    functions: [],
    web_url: '',
    icon: {
      url: 'external',
      color: '',
    },
  },
  validate: (values: FormData) => {
    const errors: FormErrors<FormData, boolean> = {};
    if (!values || Object.keys(values).length === 0) return errors;
    if (values?.name?.length === 0) errors.name = true;
    if (values?.web_url?.length === 0) errors.web_url = true;
    if (!validation.test('URL', values.web_url)) errors.web_url = true;
    return errors;
  },
})(ShortcutEditor);
